import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Egg } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EggSpinCard — homepage card that surfaces the daily Egg Spin Wheel.
 * Polls /api/spin/status when the user is signed in; shows a pulsing "Spin Now"
 * CTA if can_spin=true, or a quiet "Last spin: N 🥚 · come back tomorrow"
 * state otherwise. Hidden entirely for anon users (gated upstream).
 */
export default function EggSpinCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/spin/status`);
        if (!cancelled) setStatus(data);
      } catch {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  function openWheel() {
    window.dispatchEvent(new CustomEvent("omnimuscle:open-egg-spin"));
  }

  if (!user) return null;

  const canSpin = status?.can_spin !== false;

  return (
    <section className="py-8 bg-[#0A0A0A]" data-testid="egg-spin-card">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={openWheel}
          data-testid="egg-spin-card-cta"
          className={
            "w-full text-left border-2 transition-all p-5 flex items-center gap-4 group " +
            (canSpin
              ? "border-[#D4AF37] bg-gradient-to-r from-[#1a0d2b] via-[#0F0F0F] to-[#2b0d1a] hover:from-[#2a1d3b] hover:to-[#3b1d2a] animate-pulse-slow"
              : "border-zinc-800 bg-[#0F0F0F] hover:border-zinc-600")
          }
        >
          <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#0A0A0A] border-2 border-[#D4AF37] flex items-center justify-center text-2xl">
            🎰
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 flex items-center gap-1.5">
              <Sparkles size={11} />
              {canSpin ? "Daily Reward" : "Already Spun Today"}
            </div>
            <div className="font-display text-xl sm:text-2xl text-white leading-tight">
              {canSpin ? "Spin the Wheel · Win 1-5 Eggs" : `Last spin: ${status?.last_result || "—"} 🥚`}
            </div>
            <div className="text-xs text-neutral-400 mt-0.5">
              {canSpin
                ? "Free daily — eggs go straight to your nest."
                : "Come back tomorrow (Pacific Time)."}
            </div>
          </div>
          <div className="flex-shrink-0 hidden sm:flex items-center gap-2 text-[#D4AF37] font-bold uppercase tracking-[0.2em] text-xs">
            <Egg size={14} />
            {canSpin ? "Spin →" : ""}
          </div>
        </button>
      </div>
    </section>
  );
}
